import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fxApi, FX_DEFAULT_INSTRUMENT, FX_TIMEFRAMES } from "@/api/fxClient";
import CandlestickChart from "@/components/oanda/CandlestickChart";
import SelectCurrencyModal from "@/components/oanda/SelectCurrencyModal";
import OandaAccountCard from "@/components/oanda/OandaAccountCard";
import OandaInstrumentsScreen from "@/components/oanda/OandaInstrumentsScreen";
import Logix2FxIntelligence from "@/components/oanda/Logix2FxIntelligence";
import Logix2VoiceControls from "@/components/Logix2VoiceControls";
import ComplianceLock from "@/components/compliance/ComplianceLock";
import FxRiskCalculator from "@/components/oanda/FxRiskCalculator";

/**
 * CareLogix™ ORACLE — OANDA FX Trading Screen (Phase 22).
 *
 * Mobile-first, OANDA-style trading layout, themed in CareLogix green/gold.
 *
 *   ┌─────────────────────────────────────────────┐
 *   │  ◀  DEMO ⇆ LIVE   🎙️ LOGIX-2 voice  │ ID  │
 *   ├─────────────────────────────────────────────┤
 *   │  GBP/USD ▾    SELL 1.26064 │ 1.6 │ 1.26080 BUY │
 *   ├─────────────────────────────────────────────┤
 *   │         CANDLESTICK CHART  + AXIS            │
 *   ├─────────────────────────────────────────────┤
 *   │   2M   5M   10M   15M                        │
 *   ├─────────────────────────────────────────────┤
 *   │  ⓘ Instr · 💼 Portfolio · 📰 Feeds · ＋ New │
 *   └─────────────────────────────────────────────┘
 *
 * Props:
 *   embedded: when true, renders without page chrome — used inside the public
 *             ORACLE "oanda" mode and inside AdminOne Oanda Center.
 */

const LS_INSTRUMENT = "carelogix_fx_instrument";
const LS_TIMEFRAME = "carelogix_fx_timeframe";
const LS_MODE = "carelogix_fx_mode";
const POLL_MS = 8000;

function formatPrice(p, dec = 5) {
  if (typeof p !== "number") return "—";
  return p.toFixed(dec);
}

export default function OandaFxTradingScreen({ embedded = false } = {}) {
  // Persisted view state
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem(LS_MODE) || "demo"; } catch { return "demo"; }
  });
  const [instrument, setInstrument] = useState(() => {
    try { return localStorage.getItem(LS_INSTRUMENT) || FX_DEFAULT_INSTRUMENT; } catch { return FX_DEFAULT_INSTRUMENT; }
  });
  const [timeframe, setTimeframe] = useState(() => {
    try { return localStorage.getItem(LS_TIMEFRAME) || "5M"; } catch { return "5M"; }
  });
  const [activeTab, setActiveTab] = useState("chart"); // chart | instruments | portfolio | feeds | order | more

  // Data state
  const [account, setAccount] = useState(null);
  const [chart, setChart] = useState(null);
  const [instrData, setInstrData] = useState(null);
  const [instrumentList, setInstrumentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showSelect, setShowSelect] = useState(false);
  const [orderSide, setOrderSide] = useState(null); // "buy" | "sell"
  const [orderUnits, setOrderUnits] = useState(1000);
  const [orderResult, setOrderResult] = useState(null);
  const pollRef = useRef(null);

  // Persist user choices
  useEffect(() => { try { localStorage.setItem(LS_MODE, mode); } catch { /* ignore */ } }, [mode]);
  useEffect(() => { try { localStorage.setItem(LS_INSTRUMENT, instrument); } catch { /* ignore */ } }, [instrument]);
  useEffect(() => { try { localStorage.setItem(LS_TIMEFRAME, timeframe); } catch { /* ignore */ } }, [timeframe]);

  // Initial load — instruments list + account + chart
  const loadAccount = useCallback(async () => {
    try { const { data } = await fxApi.account(mode); setAccount(data); } catch (e) {
      setErr(e?.message || "account_failed");
    }
  }, [mode]);

  const loadInstruments = useCallback(async () => {
    try { const { data } = await fxApi.instruments(mode); setInstrData(data); } catch (e) {
      setErr(e?.message || "instruments_failed");
    }
  }, [mode]);

  const loadInstrumentList = useCallback(async () => {
    try { const { data } = await fxApi.instrumentList(); setInstrumentList(data?.items || []); } catch { /* ignore */ }
  }, []);

  const loadChart = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await fxApi.chart(instrument, timeframe, 60, mode);
      setChart(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "chart_failed");
    } finally {
      setLoading(false);
    }
  }, [instrument, timeframe, mode]);

  useEffect(() => { loadAccount(); }, [loadAccount]);
  useEffect(() => { loadInstruments(); }, [loadInstruments]);
  useEffect(() => { loadInstrumentList(); }, [loadInstrumentList]);
  useEffect(() => { loadChart(); }, [loadChart]);

  // Poll the active chart for fresh demo prices every POLL_MS.
  useEffect(() => {
    if (activeTab !== "chart") {
      if (pollRef.current) clearInterval(pollRef.current);
      return undefined;
    }
    pollRef.current = setInterval(loadChart, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeTab, loadChart]);

  // Derived
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const quote = chart?.quote || {};
  const lastPrice = useMemo(() => {
    if (typeof quote?.bid === "number" && typeof quote?.ask === "number") {
      return (quote.bid + quote.ask) / 2;
    }
    if (chart?.candles?.length) return chart.candles[chart.candles.length - 1].c;
    return undefined;
  }, [quote, chart]);
  const pctUp = (chart?.pct_change_24h ?? 0) >= 0;

  // Order staging — opens a confirmation dialog
  const stageOrder = useCallback(async (side) => {
    setOrderSide(side);
    setOrderResult(null);
  }, []);

  const confirmStage = useCallback(async () => {
    if (!orderSide) return;
    try {
      const { data } = await fxApi.orderStage({
        instrument,
        side: orderSide,
        units: Number(orderUnits) || 1000,
        type: "MARKET",
        confirm: true,
        mode,
      });
      setOrderResult(data);
    } catch (e) {
      setOrderResult({
        status: "error",
        error: e?.response?.data?.detail || e?.message || "order_stage_failed",
      });
    }
  }, [orderSide, orderUnits, instrument, mode]);

  const cancelStage = useCallback(() => {
    setOrderSide(null);
    setOrderResult(null);
  }, []);

  return (
    <div className={`fx-screen ${embedded ? "is-embedded" : ""}`} data-testid="fx-trading-screen">
      {/* Phase 26C — Compliance Lock™ banner (additive; mandated copy) */}
      <ComplianceLock variant="banner" testid="fx-compliance-lock-top" />
      {/* ───── Top bar: Demo/Live · LOGIX-2 voice overlay · Account chip ───── */}
      <header className="fx-screen__top">
        <div className="fx-screen__mode" role="group" aria-label="Account mode">
          <button
            type="button"
            className={`fx-screen__mode-btn ${mode === "demo" ? "is-active" : ""}`}
            onClick={() => setMode("demo")}
            data-testid="fx-mode-demo"
            aria-pressed={mode === "demo"}
          >DEMO</button>
          <button
            type="button"
            className={`fx-screen__mode-btn ${mode === "live" ? "is-active" : ""}`}
            onClick={() => setMode("live")}
            data-testid="fx-mode-live"
            aria-pressed={mode === "live"}
            title={account?.live_available ? "Switch to live OANDA account" : "Live keys not configured — demo data shown"}
          >LIVE{!account?.live_available && " *"}</button>
        </div>

        <div className="fx-screen__voice" data-testid="fx-voice-overlay">
          <Logix2VoiceControls
            compact
            text={`LOGIX-2. ${chart?.pair || "GBP/USD"} ${chart?.timeframe || timeframe}. Bid ${formatPrice(quote.bid, chart?.decimals || 5)}. Ask ${formatPrice(quote.ask, chart?.decimals || 5)}. Spread ${quote.spread_pips || "—"} pips. 24h change ${(chart?.pct_change_24h ?? 0).toFixed(2)} percent.`}
          />
        </div>

        <div className="fx-screen__acctchip" data-testid="fx-account-chip">
          {account?.account_id ? <code>{account.account_id}</code> : "—"}
        </div>
      </header>

      {/* ───── Pair selector ───── */}
      <button
        type="button"
        className="fx-screen__pair"
        onClick={() => setShowSelect(true)}
        aria-label="Select currency pair"
        data-testid="fx-pair-selector"
      >
        <strong>{chart?.pair || instrument.replace("_", "/")}</strong>
        <span className="fx-screen__pair-caret">▾</span>
        <span className={`fx-screen__pair-pct ${pctUp ? "is-up" : "is-down"}`}>
          {pctUp ? "▲" : "▼"} {Math.abs(chart?.pct_change_24h ?? 0).toFixed(3)}%
        </span>
      </button>

      {/* ───── Sell/Buy quote boxes + spread badge ───── */}
      <div className="fx-screen__quotes" role="group" aria-label="Quote boxes">
        <button
          type="button"
          className="fx-quote fx-quote--sell"
          onClick={() => stageOrder("sell")}
          data-testid="fx-quote-sell"
          aria-label={`Sell ${chart?.pair || instrument} at ${formatPrice(quote.bid, chart?.decimals || 5)}`}
        >
          <span className="fx-quote__label">SELL</span>
          <strong className="fx-quote__price">{formatPrice(quote.bid, chart?.decimals || 5)}</strong>
        </button>
        <div className="fx-quote__spread" data-testid="fx-quote-spread">
          <span className="fx-quote__spread-num">{quote.spread_pips ?? "—"}</span>
          <span className="fx-quote__spread-label">pips</span>
        </div>
        <button
          type="button"
          className="fx-quote fx-quote--buy"
          onClick={() => stageOrder("buy")}
          data-testid="fx-quote-buy"
          aria-label={`Buy ${chart?.pair || instrument} at ${formatPrice(quote.ask, chart?.decimals || 5)}`}
        >
          <span className="fx-quote__label">BUY</span>
          <strong className="fx-quote__price">{formatPrice(quote.ask, chart?.decimals || 5)}</strong>
        </button>
      </div>

      {err && <div className="fx-screen__err" role="alert" data-testid="fx-error">{err}</div>}

      {/* ───── Active screen ───── */}
      {activeTab === "chart" && (
        <section className="fx-screen__chart-wrap" data-testid="fx-chart-wrap">
          {loading && !chart && <div className="fx-screen__loading">Loading {timeframe} chart…</div>}
          {chart && (
            <CandlestickChart
              candles={chart.candles || []}
              decimals={chart.decimals || 5}
              lastPrice={lastPrice}
              height={embedded ? 340 : 380}
            />
          )}
          <div className="fx-screen__tfrow" role="tablist" aria-label="Timeframe">
            {FX_TIMEFRAMES.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={timeframe === t}
                className={`fx-tf ${timeframe === t ? "is-active" : ""}`}
                onClick={() => setTimeframe(t)}
                data-testid={`fx-tf-${t}`}
              >{t}</button>
            ))}
          </div>
          <Logix2FxIntelligence instrument={instrument} timeframe={timeframe} compact={embedded} />
        </section>
      )}

      {activeTab === "instruments" && (
        <section className="fx-screen__instr-wrap" data-testid="fx-instruments-wrap">
          <OandaInstrumentsScreen
            data={instrData}
            onPick={(inst) => { setInstrument(inst); setActiveTab("chart"); }}
          />
        </section>
      )}

      {activeTab === "portfolio" && (
        <section className="fx-screen__pf" data-testid="fx-portfolio">
          <OandaAccountCard
            account={account}
            onTrade={() => setActiveTab("chart")}
            onAddFunds={() => alert("ADD FUNDS is disabled in DEMO mode. Research-only.")}
            onSignOut={() => alert("SIGN OUT requires real OANDA account binding. Not enabled in DEMO mode.")}
          />
        </section>
      )}

      {activeTab === "feeds" && (
        <section className="fx-screen__feeds" data-testid="fx-feeds">
          <h3>Market Feeds · LOGIX-2 commentary</h3>
          <p className="fx-discl">{chart?.disclaimer || ""}</p>
          <ul className="fx-feeds-list">
            <li>🟢 ORACLE Signal Fabric™ scanning {chart?.pair || instrument.replace("_", "/")}</li>
            <li>🟡 Watch for {timeframe} session momentum reversal</li>
            <li>🔵 LOGIX-2 recommends human review before any high-risk entry</li>
          </ul>
        </section>
      )}

      {activeTab === "order" && (
        <section className="fx-screen__order" data-testid="fx-new-order">
          <h3>New Order · {chart?.pair || instrument.replace("_", "/")}</h3>
          <div className="fx-screen__order-row">
            <label>Units
              <input
                type="number"
                min="100"
                step="100"
                value={orderUnits}
                onChange={(e) => setOrderUnits(parseInt(e.target.value || "1000", 10))}
                data-testid="fx-new-order-units"
              />
            </label>
            <div className="fx-screen__order-actions">
              <button type="button" className="fx-btn fx-btn--sell" onClick={() => stageOrder("sell")} data-testid="fx-new-order-sell">SELL {orderUnits}</button>
              <button type="button" className="fx-btn fx-btn--buy" onClick={() => stageOrder("buy")} data-testid="fx-new-order-buy">BUY {orderUnits}</button>
            </div>
          </div>
          <p className="fx-discl">All orders are research-only and require explicit confirmation. No automatic execution.</p>
        </section>
      )}

      {activeTab === "more" && (
        <section className="fx-screen__more" data-testid="fx-more">
          <h3>More · {account?.user_name || "Demo Trader"}</h3>
          <OandaAccountCard
            account={account}
            onTrade={() => setActiveTab("chart")}
            onAddFunds={() => alert("ADD FUNDS is disabled in DEMO mode.")}
            onSignOut={() => alert("SIGN OUT requires real OANDA account binding.")}
          />
        </section>
      )}

      {activeTab === "risk" && (
        <section className="fx-screen__risk" data-testid="fx-risk-section">
          <FxRiskCalculator
            instrument={instrument}
            quote={quote}
            decimals={chart?.decimals || 5}
            embedded
          />
        </section>
      )}

      {/* ───── Bottom navigation ───── */}
      <nav className="fx-screen__bottom" role="navigation" aria-label="FX trading sections">
        {[
          { id: "instruments", icon: "ⓘ", label: "Instruments" },
          { id: "portfolio",   icon: "💼", label: "Portfolio" },
          { id: "feeds",       icon: "📰", label: "Feeds" },
          { id: "order",       icon: "＋", label: "New Order" },
          { id: "risk",        icon: "⚖", label: "Risk" },
          { id: "more",        icon: "⋯", label: "More" },
        ].map((b) => (
          <button
            key={b.id}
            type="button"
            className={`fx-screen__bnav ${activeTab === b.id ? "is-active" : ""}`}
            onClick={() => setActiveTab(b.id)}
            data-testid={`fx-bnav-${b.id}`}
            aria-current={activeTab === b.id ? "page" : undefined}
          >
            <span className="fx-screen__bnav-icon" aria-hidden="true">{b.icon}</span>
            <span className="fx-screen__bnav-lbl">{b.label}</span>
          </button>
        ))}
      </nav>

      {/* ───── Disclaimer ───── */}
      <footer className="fx-screen__discl" data-testid="fx-disclaimer">
        <em>{chart?.disclaimer || "Research only. Forex trading involves substantial risk of loss. No automatic trade execution without user confirmation."}</em>
      </footer>

      {/* Phase 26C — Compliance Lock™ footer (additive; mandated copy) */}
      <ComplianceLock variant="footer" testid="fx-compliance-lock-footer" />

      {/* ───── Select Currency modal ───── */}
      <SelectCurrencyModal
        open={showSelect}
        instruments={instrumentList}
        selectedInstrument={instrument}
        onSelect={(inst) => { setInstrument(inst); setShowSelect(false); }}
        onClose={() => setShowSelect(false)}
      />

      {/* ───── Order confirmation dialog ───── */}
      {orderSide && (
        <div className="fx-modal" role="dialog" aria-modal="true" data-testid="fx-order-confirm-modal" onClick={(e) => { if (e.target === e.currentTarget) cancelStage(); }}>
          <div className="fx-modal__card">
            <header className="fx-modal__head">
              <strong>Confirm {orderSide.toUpperCase()} · {chart?.pair || instrument.replace("_", "/")}</strong>
              <button type="button" className="fx-modal__close" onClick={cancelStage} aria-label="Close">×</button>
            </header>
            <div style={{ padding: 14, lineHeight: 1.5 }}>
              <p>Side: <strong>{orderSide.toUpperCase()}</strong></p>
              <p>Units: <strong>{orderUnits}</strong></p>
              <p>Reference price: <strong>{orderSide === "buy" ? formatPrice(quote.ask, chart?.decimals || 5) : formatPrice(quote.bid, chart?.decimals || 5)}</strong></p>
              {!orderResult && (
                <>
                  <p style={{ color: "#f5c842", fontSize: 12.5 }}>
                    Research-only. <strong>No automatic execution.</strong> Click STAGE to record the staged order; you can review it under Compliance before any future live-execution flow is enabled.
                  </p>
                  <div className="fx-modal__row-actions">
                    <button type="button" className="fx-btn" onClick={cancelStage} data-testid="fx-order-cancel">CANCEL</button>
                    <button type="button" className={`fx-btn fx-btn--${orderSide === "buy" ? "buy" : "sell"}`} onClick={confirmStage} data-testid="fx-order-stage-confirm">STAGE {orderSide.toUpperCase()}</button>
                  </div>
                </>
              )}
              {orderResult && (
                <div className="fx-order-result" data-testid="fx-order-result">
                  <p><strong>Status:</strong> {orderResult.status}</p>
                  <p><strong>Mode:</strong> {orderResult.mode}</p>
                  <p><strong>Would execute:</strong> {String(orderResult.would_execute)}</p>
                  <p><strong>Live capable:</strong> {String(orderResult.live_capable)}</p>
                  {orderResult.next_step && <p>{orderResult.next_step}</p>}
                  {orderResult.error && <p style={{ color: "#e84a4a" }}>{orderResult.error}</p>}
                  <div className="fx-modal__row-actions">
                    <button type="button" className="fx-btn" onClick={cancelStage} data-testid="fx-order-close">CLOSE</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
